import { faArrowLeft, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";

interface RightSlidingPanelType {
  show: boolean;
  title: string;
  align?: string;
  children: ReactNode;
  addNewSpace?: Function | null;
  onClosePanel: Function;
}

const RightSlidingPanel: React.FC<RightSlidingPanelType> = ({
  show,
  title,
  align = "right",
  children,
  addNewSpace = null,
  onClosePanel,
}) => {
  const [showPanel, setShowPanel] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const closePanel = () => {
    setShowPanel(false);

    setTimeout(() => {
      onClosePanel();
    }, 400);
  };

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        setShowPanel(true);
      }, 250);
    }
  }, []);

  // ✅ Close the panel when clicking anywhere outside it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only react once the panel has finished sliding in.
      if (!showPanel) return;

      // Use the event path captured at dispatch time. Some controls (e.g. the
      // tag dropdown suggestions) remove the clicked element from the DOM during
      // their own mousedown handler, which would make a later target.closest()
      // run on a detached node and wrongly read the click as "outside" — closing
      // the panel/modal. composedPath() is immune to that mutation.
      const path = (event.composedPath?.() || []) as EventTarget[];
      const target = event.target as HTMLElement;

      const isInside = (matcher: (el: HTMLElement) => boolean) => {
        if (path.length) {
          return path.some((el) => el instanceof HTMLElement && matcher(el));
        }
        // Fallback for environments without composedPath.
        return !!target.closest && matcher(target);
      };

      // Ignore clicks inside the panel itself.
      if (
        isInside((el) => !!dropdownRef.current && dropdownRef.current === el)
      ) {
        return;
      }

      // Ignore clicks inside any open modal/dialog launched from the panel
      // (e.g. Add/Edit Space or confirmation dialogs) so those don't close it.
      if (
        isInside(
          (el) =>
            el.classList?.contains("modal") ||
            el.classList?.contains("modal-dialog")
        )
      ) {
        return;
      }

      closePanel();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPanel]);

  const slidingPanelClass = showPanel ? "slide-in" : "";
  const slidingPanelSide = align === "right" ? "right-panel" : "left-panel";

  return (
    <div
      ref={dropdownRef}
      className={`sliding-panel ${slidingPanelSide} ${slidingPanelClass}`}
    >
      <div className="sliding-panel-header">
        <span className="back-icon" onClick={() => closePanel()}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </span>

        <h2>{title}</h2>

        {addNewSpace && (
          <Button
            variant="primary"
            className="add-space-btn"
            onClick={() => addNewSpace()}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Space
          </Button>
        )}
      </div>

      <div className="sliding-panel-content">{children}</div>
    </div>
  );
};

export default RightSlidingPanel;
